/**
 * fetchAllPages — permanently safe alternative to hardcoded size limits.
 *
 * Works with any Spring-paginated API function that accepts { page, size } params
 * and returns a response where res.data is a Spring Page object:
 *   { content: [...], totalElements: N, totalPages: N, ... }
 *
 * Usage:
 *   const allLevels = await fetchAllPages((params) => listLevels('ADMIN', params));
 *   const allBatches = await fetchAllPages((params) => listBatches('ADMIN', params));
 *
 * Strategy:
 *   1. Fetch page 0 with a reasonable page size (100)
 *   2. Read totalPages from the response
 *   3. Fetch all remaining pages in parallel
 *   4. Return flat combined content array
 *
 * If the response is not paginated (plain array), returns it directly.
 */
export async function fetchAllPages(apiFn, pageSize = 100) {
  const first = await apiFn({ page: 0, size: pageSize });
  const data = first.data;

  // Not a paginated response — plain array or unknown shape
  if (!data || !Array.isArray(data.content)) {
    return Array.isArray(data) ? data : (data?.content ?? []);
  }

  const totalPages = data.totalPages ?? 1;
  const content = [...data.content];

  if (totalPages <= 1) return content;

  // Fetch all remaining pages in parallel
  const remaining = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) =>
      apiFn({ page: i + 1, size: pageSize }).then((r) => r.data?.content ?? [])
    )
  );

  return content.concat(...remaining);
}
